import React, { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popper,
  Typography,
  Link,
  Grow,
  Switch,
  Tab,
  Tabs,
  useMediaQuery,
} from "@mui/material";
import parse from "html-react-parser";
import { makeStyles } from "@mui/styles";
import moment from "moment";

//... Service imports
import { PushNotifications } from "../../services/PushNotifications.Service";

//... Icons imports
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import MarkUnreadIcon from "@mui/icons-material/MarkUnreadChatAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

//... Shared Components
import AlertSnackbar from "../../sharedComponents/Snackbar/AlertSnackbar";
import TitleTooltip from "../Tooltip/TitleTooltip";
import Helper from "../../common/Helper";
import ConfirmationDialog from "../dialog/ConfirmationDialog";

const useStyles = makeStyles((theme) => ({
  notificationHeaderContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    fontSize: "clamp(0.6rem, 1vw + 0.6rem, 1.25rem)",
    //backgroundColor: "#1d428a",
    //color: "white",
    borderRadius: 0,
  },
  notificationHeaderTxt: {
    padding: theme.spacing(1.6, 2),
  },
  notificationItemText: {
    fontSize: "clamp(0.3rem, 1vw + 0.3rem, 1rem)",
  },
  notificationSecondaryText: {
    fontSize: "clamp(0.3rem, 1vw + 0.3rem, 0.8rem)",
  },
  avatar: {
    backgroundColor: "#1d428a !important",
    color: "#fff",
    fontSize: "clamp(0.6rem, 1vw + 0.6rem, 1rem)",
    width: "clamp(20px, 3vw + 20px, 40px)",
    height: "clamp(20px, 3vw + 20px, 40px)",
  },
  iconButton: {
    fontSize: 20,
  },
  notificationBody: {
    fontSize: "clamp(0.3rem, 1vw + 0.3rem, 0.8rem)",
  },
  markAsReadButton: {
    marginTop: theme.spacing(1),
  },
  deleteButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  list: {
    height: "60vh",
    overflow: "auto",

    // Webkit scrollbar styling
    "&::-webkit-scrollbar": {
      width: "10px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#1d428a",
      borderRadius: "10px",
      border: "2px solid #f1f1f1",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
  noResultContainer: {
    minHeight: "60vh",
  },
  link: {
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: "clamp(0.6rem, 1vw + 0.6rem, 1rem)",
    fontWeight: "bold",
    transition: "color 0.3s ease, transform 0.3s ease",

    "&:hover": {
      transform: "scale(1.05)",
      textDecoration: "none",
    },

    "&:active": {
      color: "#ff4500",
      transform: "scale(1)",
    },
  },
  listItem: {
    "&:hover": {
      cursor: "pointer",
      background: "linear-gradient(to right, #f0f4f8, #e0e8f0)",
    },
  },
  readNotification: {
    opacity: 0.6,
    backgroundColor: "#f5f5f5",
  },
  readNotificationText: {
    color: "#777",
  },
  readAvatar: {
    backgroundColor: "#ccc !important",
    color: "#666 !important",
  },
}));

const extractDetails = (title) => {
  const parts = title.split("NBA-");

  const namePart = parts[1]?.trim();
  const extractedName = namePart?.split(" ").slice(0, 2).join(" ");
  const extractedContent = namePart?.slice(extractedName.length).trim();

  return {
    extractedName: extractedName,
    extractedContent: extractedContent,
  };
};

const extractSecondPTag = (html) => parse(html)?.[1]?.props?.children || "";

const NotificationPopover = ({ user }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [expandedNotificationId, setExpandedNotificationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openNotificationDelDialog, setOpenNotificationDelDialog] =
    useState(false);
  const [openNotificationDelAllDialog, setOpenNotificationDelAllDialog] =
    useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "warning",
    message: "",
  });

  const [selectedTab, setSelectedTab] = useState("unread");
  const [unreadCount, setUnreadCount] = useState(0);
  const [readCount, setReadCount] = useState(0);

  const anchorRef = useRef(null);
  const notificationCountRef = useRef(0);
  const selectedNotification = useRef(null);
  const listRef = useRef(null);

  const isSmallScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("xs", "sm")
  );

  const isMediamScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("xs", "md")
  );
  useEffect(() => {
    if (user.userID) {
      //...Inital fetch
      fetchNotifications();

      if (user.pushNotificationTimeStamp > 0) {
        const intervalId = setInterval(
          fetchNotifications,
          user.pushNotificationTimeStamp
        );
        return () => clearInterval(intervalId);
      }
    }
  }, [user]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const { data } =
        await PushNotifications.getPushNotificationForLoggedInUser(user.userID);
      setNotifications(data || []);

      // Update counts for unread and read notifications
      const unreadNotifications = data.filter(
        ({ notificationRead }) => !notificationRead
      );
      const readNotifications = data.filter(
        ({ notificationRead }) => notificationRead
      );

      setUnreadCount(unreadNotifications.length);
      setReadCount(readNotifications.length);

      notificationCountRef.current = getUnReadMessageCount(data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUnReadMessageCount = (notifications) =>
    notifications?.filter(({ notificationRead }) => !notificationRead).length ||
    0;

  //... Handlers
  const showAlert = (type, message) => setAlert({ open: true, type, message });

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) =>
    !anchorRef.current?.contains(event.target) && setOpen(false);

  const handleExpandClick = (id) => {
    setExpandedNotificationId((prevId) => (prevId === id ? null : id));

    setTimeout(() => {
      const listItem = document.getElementById(id);
      if (listItem) {
        listItem.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const onAllMarkAsReadAgree = async () => {
    await markAllNotificationsAsReadHandler();
  };

  const handleDeleteAllPushNotifications = (e) => {
    e.stopPropagation();
    setOpenNotificationDelAllDialog(true);
  };

  const onAllClearAllAgree = async (e) => {
    e.stopPropagation();
    setOpenNotificationDelAllDialog(false);
    await clearAllNotificationsHandler();
  };

  const handleDeletePushNotification = (e, notificationID) => {
    e.stopPropagation();
    selectedNotification.current = notificationID;
    setOpenNotificationDelDialog(true);
  };

  const onDeleteNotification = async (e) => {
    e.stopPropagation();
    setOpenNotificationDelDialog(false);
    await deleteNotification(selectedNotification.current);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const filteredNotifications = notifications.filter((notif) => {
    if (selectedTab === "read") return notif.notificationRead;
    if (selectedTab === "unread") return !notif.notificationRead;
    return true;
  });

  //... Service calls
  const handleMarkAsRead = async ({ notificationID, notificationRead }) => {
    try {
      if (
        await PushNotifications.updateReadStatus({
          NotificationId: notificationID,
          MarkAsRead: !notificationRead,
          ProcessedBy: user.userID,
        })
      ) {
        await fetchNotifications();
        showAlert(
          "success",
          `Notification marked as ${!notificationRead ? "read" : "unread"}.`
        );
      }
    } catch (error) {
      console.error("Error updating notification:", error);
      showAlert("error", "Failed to update notification.");
    }
  };

  const markAllNotificationsAsReadHandler = async () => {
    try {
      const result = await PushNotifications.markNotificationsAsReadForAll({
        MarkAllRead: true,
        ProcessedBy: user.userID,
        UserId: user.userID,
      });

      if (result) {
        await fetchNotifications();
        showAlert(
          "success",
          "All notifications have been successfully marked as read....."
        );
      }
    } catch (error) {
      console.error("Error updating notifications:", error);
      showAlert("error", "An error occurred...");
    }
  };

  const clearAllNotificationsHandler = async () => {
    try {
      const result = await PushNotifications.deletePushNotification({
        ProcessedBy: user.userID,
      });

      if (result) {
        await fetchNotifications();
        showAlert(
          "success",
          "All notifications have been successfully deleted....."
        );
      }
    } catch (error) {
      console.error("Error updating notifications:", error);
      showAlert("error", "An error occurred...");
    }
  };

  const deleteNotification = async (notificationID) => {
    try {
      const result = await PushNotifications.deletePushNotification({
        NotificationId: notificationID,
        ProcessedBy: user.userID,
      });

      if (result) {
        await fetchNotifications();
        selectedNotification.current = null;
        showAlert("success", "Notifications deleted successfully.....");
      }
    } catch (error) {
      console.error("Error updating notifications:", error);
      showAlert("error", "An error occurred...");
    }
  };

  return (
    <div>
      <IconButton ref={anchorRef} color="inherit" onClick={handleToggle}>
        <Badge
          badgeContent={notificationCountRef.current || 0}
          color="error"
          showZero
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal={true}
        placement={isSmallScreen ? "bottom-end" : undefined}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper
              sx={{
                width: { xs: 320, sm: 320, md: 470, lg: 470, xl: 480 },
                boxShadow: 3,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* Header Section */}
                  <Box className={classes.notificationHeaderContainer}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      className={classes.notificationHeaderTxt}
                    >
                      <Typography variant="h6">Notifications</Typography>

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        {notifications?.length > 0 && (
                          <>
                            <Box style={{ paddingRight: "8px" }}>
                              {unreadCount > 0 && (
                                <>
                                  {!isMediamScreen ? (
                                    <Link
                                      className={classes.link}
                                      onClick={onAllMarkAsReadAgree}
                                    >
                                      Mark All as Read
                                    </Link>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={onAllMarkAsReadAgree}
                                      className={classes.btnIcon}
                                      sx={{
                                        minWidth: "32px",
                                        padding: "2px 6px",
                                      }}
                                    >
                                      <MarkUnreadIcon />
                                    </Button>
                                  )}
                                </>
                              )}
                            </Box>
                            {"|"}
                            <Box style={{ padding: "0 8px" }}>
                              {notifications?.length > 0 && (
                                <>
                                  {!isMediamScreen ? (
                                    <Link
                                      className={classes.link}
                                      onClick={(e) =>
                                        handleDeleteAllPushNotifications(e)
                                      }
                                    >
                                      Clear All
                                    </Link>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={(e) =>
                                        handleDeleteAllPushNotifications(e)
                                      }
                                      className={classes.btnIcon}
                                      sx={{
                                        minWidth: "32px",
                                        padding: "2px 6px",
                                      }}
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  )}
                                </>
                              )}
                            </Box>
                          </>
                        )}

                        <TitleTooltip title="Refresh" placement="bottom">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={fetchNotifications}
                            className={classes.btnIcon}
                            sx={{ minWidth: "32px", padding: "2px 6px" }}
                          >
                            <RefreshOutlinedIcon
                              className={isLoading ? "rotate-icon" : ""}
                            />
                          </Button>
                        </TitleTooltip>
                      </Box>
                    </Box>

                    {/* Tabs inside header */}
                    <Tabs
                      value={selectedTab}
                      onChange={handleTabChange}
                      textColor="primary"
                      sx={{
                        backgroundColor: "transparent",
                        "& .MuiTabs-indicator": {
                          backgroundColor: "#1d428a",
                          height: "3px",
                        },
                        "& .MuiTab-root": {
                          fontWeight: "bold",
                        },
                      }}
                    >
                      <Tab label={`Unread (${unreadCount})`} value="unread" />
                      <Tab label={`Read (${readCount})`} value="read" />
                      <Tab
                        label={`All (${notifications.length})`}
                        value="all"
                      />
                    </Tabs>
                  </Box>

                  <Divider />
                  {/* Content Section */}
                  <Box>
                    {filteredNotifications.length === 0 ? (
                      <Box className={classes.noResultContainer}>
                        <Typography align="center" sx={{ py: 2 }}>
                          No new notifications available.
                        </Typography>
                      </Box>
                    ) : (
                      <List className={classes.list} ref={listRef}>
                        {filteredNotifications.map((notif) => {
                          const { extractedName, extractedContent } =
                            extractDetails(notif.title);

                          const parsedHtml = parse(notif.notificationtext, {
                            replace: (domNode) => {
                              if (domNode.name === "p") {
                                return (
                                  <p className={classes.notificationBody}>
                                    {domNode.children[0]?.data}
                                  </p>
                                );
                              }
                              return domNode;
                            },
                          });

                          return (
                            <React.Fragment key={notif.notificationID}>
                              <ListItem
                                alignItems="flex-start"
                                sx={{ cursor: "pointer" }}
                                id={notif.notificationID}
                                className={`${classes.listItem}`}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    className={`${classes.avatar} ${
                                      notif.notificationRead
                                        ? classes.readAvatar
                                        : ""
                                    }`}
                                  >
                                    {Helper.GetNameInitials(extractedName)}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="subtitle2"
                                      className={`${
                                        classes.notificationItemText
                                      } ${
                                        notif.notificationRead
                                          ? classes.readNotificationText
                                          : ""
                                      }`}
                                    >
                                      {extractedName}{" "}
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        className={`${
                                          classes.notificationItemText
                                        } ${
                                          notif.notificationRead
                                            ? classes.readNotificationText
                                            : ""
                                        }`}
                                      >
                                        {extractedContent}
                                      </Typography>
                                    </Typography>
                                  }
                                  // secondary={
                                  //   <Typography
                                  //     variant="caption"
                                  //     className={`${
                                  //       classes.notificationSecondaryText
                                  //     } ${
                                  //       notif.notificationRead
                                  //         ? classes.readNotificationText
                                  //         : ""
                                  //     }`}
                                  //   >
                                  //     {`${extractSecondPTag(
                                  //       notif.notificationtext
                                  //     )} - ${moment(
                                  //       notif.createdDate
                                  //     ).fromNow()}`}
                                  //   </Typography>
                                  // }
                                />
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <TitleTooltip
                                    title={
                                      notif.notificationRead
                                        ? "Mark As Unread"
                                        : "Mark As Read"
                                    }
                                    placement="bottom"
                                  >
                                    <Switch
                                      checked={notif.notificationRead}
                                      onChange={() => handleMarkAsRead(notif)}
                                      color="primary"
                                      size="small"
                                    />
                                  </TitleTooltip>

                                  <TitleTooltip
                                    title="Delete Notification"
                                    placement="bottom"
                                  >
                                    <IconButton size="small">
                                      <DeleteOutlineIcon
                                        color="error"
                                        className={classes.iconButton}
                                        onClick={(e) =>
                                          handleDeletePushNotification(
                                            e,
                                            notif.notificationID
                                          )
                                        }
                                      />
                                    </IconButton>
                                  </TitleTooltip>

                                  {/* <IconButton
                                    size="small"
                                    onClick={(e) =>
                                      handleExpandClick(notif.notificationID)
                                    }
                                  >
                                    <ExpandMoreIcon
                                      sx={{
                                        transform:
                                          expandedNotificationId ===
                                          notif.notificationID
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        transition: "transform 0.3s",
                                      }}
                                    />
                                  </IconButton> */}
                                </Box>
                              </ListItem>

                              <Collapse
                                in={
                                  expandedNotificationId ===
                                  notif.notificationID
                                }
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ pl: 7, pt: 1 }}>
                                  <Typography variant="body2">
                                    {parsedHtml}
                                  </Typography>
                                </Box>
                              </Collapse>
                              <Divider />
                            </React.Fragment>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <AlertSnackbar
        open={alert.open}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ConfirmationDialog
        open={openNotificationDelDialog}
        message="Are you sure you want to delete notification?"
        handleClose={() => setOpenNotificationDelDialog(false)}
        handleAgree={onDeleteNotification}
        handleDisAgree={() => setOpenNotificationDelDialog(false)}
        maxWidth="sm"
      />

      <ConfirmationDialog
        open={openNotificationDelAllDialog}
        message="Are you sure you want to delete all notifications?"
        handleClose={() => setOpenNotificationDelAllDialog(false)}
        handleAgree={(e) => onAllClearAllAgree(e)}
        handleDisAgree={() => setOpenNotificationDelAllDialog(false)}
        maxWidth="sm"
      />
    </div>
  );
};

export default NotificationPopover;
